/**
 * sqlite3mt WAL valid-prefix scan helpers
 *
 * Mechanical extraction from §96 §7.10, §7.5 and §7.7/§7.8.
 *
 * These functions implement the core WAL scanning primitives needed for
 * bootstrap recovery:
 *
 *   buildValidWalPrefix()
 *     — validates WAL header and cumulative frame checksums from frame 1
 *     — returns the highest valid frame index (SQLite valid-prefix semantics)
 *     — §7.10, R70: stops at first invalid/truncated frame
 *
 *   scanWalCommittedTransactions()
 *     — emits committed transactions within a frame range
 *     — §7.10: postPageCount from commit-frame nPage, not Page 1
 *
 *   scanExactCommittedTransaction()
 *     — exact match verification for APPENDING intent disposition
 *     — §7.5: FOUND_EXACT_MATCH | NO_MATCH | CORRUPT
 *
 * All checksums are bit-exact SQLite WAL checksums from walChecksum.ts.
 * The checksum chain always starts from the WAL header and is cumulative;
 * there is no "restart" of the chain at an arbitrary mid-point frame.
 */

import {
    WAL_HDR_SIZE,
    WAL_FRAME_HDR_SIZE,
    WAL_MAGIC_LE,
    WAL_FORMAT_VERSION,
    parseWalHeader,
    walChecksumBytes,
    computeWalHeaderChecksum,
} from './wal-checksum'
import {
    type CommittedTx,
    type ExactScanResult,
    type RawCommittedTx,
    type WalFrameEntry,
    type Delta,
    type FrameMax,
    type PageHash,
    type Pgno,
} from './types'

// ── §7.10 buildValidWalPrefix ───────────────────────────────────

/**
 * Validate the WAL prefix from the header through endFrameInclusive.
 *
 * §7.10 / R70:
 * - Verifies WAL header checksum
 * - Iterates frames 1..endFrameInclusive verifying:
 *   - full frame is physically available
 *   - pgno >= 1
 *   - salt1/salt2 match WAL header
 *   - cumulative frame checksum matches SQLite's algorithm exactly
 * - Stops at first invalid/truncated frame
 *
 * @returns highest valid frame index (0 = only header valid, -1 = header invalid)
 */
export function buildValidWalPrefix(
    walBytes: Uint8Array,
    endFrameInclusive: number,
    pageSize: number
): number {
    if (walBytes.length < WAL_HDR_SIZE) return -1

    const header = parseWalHeader(walBytes.subarray(0, WAL_HDR_SIZE))
    if (header == null) return -1
    if (header.version !== WAL_FORMAT_VERSION) return -1
    if (header.pageSize !== pageSize) return -1

    // Verify WAL header checksum
    const expectedHdrCksum = computeWalHeaderChecksum(header.nativeCksum, walBytes)
    if (expectedHdrCksum[0] !== header.cksum1 || expectedHdrCksum[1] !== header.cksum2) {
        return -1
    }

    if (endFrameInclusive <= 0) return 0

    const frameSize = WAL_FRAME_HDR_SIZE + pageSize
    let runningCksum: [number, number] = [header.cksum1, header.cksum2]

    for (let frameIdx = 1; frameIdx <= endFrameInclusive; frameIdx++) {
        const frameOff = WAL_HDR_SIZE + (frameIdx - 1) * frameSize

        // Check the frame is fully present
        if (frameOff + frameSize > walBytes.length) {
            return frameIdx - 1
        }

        // Read frame header fields
        const fhView = new DataView(
            walBytes.buffer, walBytes.byteOffset + frameOff, WAL_FRAME_HDR_SIZE
        )
        const pgno = fhView.getUint32(0, false)
        // const nTruncate = fhView.getUint32(4, false) — not needed for validation
        const salt1 = fhView.getUint32(8, false)
        const salt2 = fhView.getUint32(12, false)
        const storedCk1 = fhView.getUint32(16, false)
        const storedCk2 = fhView.getUint32(20, false)

        // pgno must be >= 1
        if (pgno < 1) return frameIdx - 1

        // Salt must match WAL header
        if (salt1 !== header.salt1 || salt2 !== header.salt2) return frameIdx - 1

        // Compute cumulative checksum: frame_header[0..7] + page_data
        const hdr8 = new Uint8Array(8)
        const hdr8View = new DataView(hdr8.buffer)
        hdr8View.setUint32(0, pgno, false)
        hdr8View.setUint32(4, fhView.getUint32(4, false), false)

        const afterHdr8 = walChecksumBytes(header.nativeCksum, hdr8, runningCksum)
        const pageData = new Uint8Array(
            walBytes.buffer, walBytes.byteOffset + frameOff + WAL_FRAME_HDR_SIZE, pageSize
        )
        const computed = walChecksumBytes(header.nativeCksum, pageData, afterHdr8)

        if (computed[0] !== storedCk1 || computed[1] !== storedCk2) {
            return frameIdx - 1
        }

        runningCksum = computed
    }

    return endFrameInclusive
}

// ── §7.10 Internal frame parsing ────────────────────────────────

/**
 * Parse all frames in the valid prefix and group them into committed
 * transactions. Only frames whose commit frame (nTruncate > 0) lies
 * within (loFrameExclusive, validHi] are emitted.
 *
 * §7.10: postPageCount comes from commit-frame's nPage field.
 * §7.10: No frames beyond the first invalid frame are ever used.
 */
export function parseCommittedTransactionsInRange(
    walBytes: Uint8Array,
    pageSize: number,
    validHi: number,
    loFrameExclusive: number
): RawCommittedTx[] {
    if (validHi <= loFrameExclusive) return []
    if (walBytes.length < WAL_HDR_SIZE) return []

    const header = parseWalHeader(walBytes.subarray(0, WAL_HDR_SIZE))
    if (header == null) return []

    const frameSize = WAL_FRAME_HDR_SIZE + pageSize
    const results: RawCommittedTx[] = []
    let currentFrames: WalFrameEntry[] = []

    for (let frameIdx = 1; frameIdx <= validHi; frameIdx++) {
        const frameOff = WAL_HDR_SIZE + (frameIdx - 1) * frameSize
        if (frameOff + frameSize > walBytes.length) break

        const fhView = new DataView(
            walBytes.buffer, walBytes.byteOffset + frameOff, WAL_FRAME_HDR_SIZE
        )
        const pgno = fhView.getUint32(0, false)
        const nPage = fhView.getUint32(4, false)

        const pageData = new Uint8Array(
            walBytes.buffer,
            walBytes.byteOffset + frameOff + WAL_FRAME_HDR_SIZE,
            pageSize
        ).slice()

        currentFrames.push({ frameIdx, pgno, nPage, pageData })

        if (nPage > 0) {
            // Commit frame — emit if within range
            if (frameIdx > loFrameExclusive) {
                results.push({
                    frameMax: frameIdx,
                    frames: currentFrames,
                    postPageCount: nPage,
                })
            }
            currentFrames = []
        }
    }

    return results
}

// ── §7.10 scanWalCommittedTransactions ───────────────────────────

/**
 * A function that computes the content hash for a page image.
 * Injected to keep this module free from specific hash implementations.
 */
export type HashPageFn = (pageData: Uint8Array) => PageHash

/**
 * Scan the WAL for committed transactions within a frame range.
 *
 * §7.10:
 * - Validates the WAL prefix up to hiFrameInclusive
 * - Emits only committed transactions whose commit-frame lies within
 *   (loFrameExclusive, validHi]
 * - delta.postPageCount is set from commit-frame's nPage field
 * - page 1 bytes may be used as validation but NOT as sole EOF source
 *
 * @param walBytes     Raw WAL file bytes
 * @param pageSize     Database page size
 * @param walSalt0     Expected WAL salt-0 (must match header)
 * @param walSalt1     Expected WAL salt-1 (must match header)
 * @param loFrameExclusive  Exclusive lower bound (typically integratedFrameMax)
 * @param hiFrameInclusive  Inclusive upper bound (typically live.mxFrame)
 * @param hashPage     Function to hash a page image → PageHash
 * @returns committed transactions in frameMax order
 */
export function scanWalCommittedTransactions(
    walBytes: Uint8Array,
    pageSize: number,
    walSalt0: number,
    walSalt1: number,
    loFrameExclusive: number,
    hiFrameInclusive: number,
    hashPage: HashPageFn
): CommittedTx[] {
    const validHi = buildValidWalPrefix(walBytes, hiFrameInclusive, pageSize)
    if (validHi <= loFrameExclusive) return []

    // Verify the WAL salts match expectations
    const header = parseWalHeader(walBytes.subarray(0, WAL_HDR_SIZE))
    if (header == null) return []
    if (header.salt1 !== walSalt0 || header.salt2 !== walSalt1) return []

    const rawTxs = parseCommittedTransactionsInRange(
        walBytes, pageSize, validHi, loFrameExclusive
    )

    const results: CommittedTx[] = []
    for (const raw of rawTxs) {
        const pageHashes = new Map<Pgno, PageHash>()
        const blobs = new Map<PageHash, Uint8Array>()

        for (const frame of raw.frames) {
            const hash = hashPage(frame.pageData)
            pageHashes.set(frame.pgno as Pgno, hash)
            blobs.set(hash, frame.pageData)
        }

        results.push({
            frameMax: raw.frameMax as FrameMax,
            delta: {
                pageHashes,
                postPageCount: raw.postPageCount,
            },
            blobs,
        })
    }

    return results
}

// ── §7.5 scanExactCommittedTransaction ──────────────────────────

/**
 * Verify that a specific frame interval contains an exact committed
 * transaction matching expectedDelta.
 *
 * §7.5 / §7.10:
 * - Validates the WAL prefix up to endFrame (buildValidWalPrefix)
 * - The checksum chain is NOT restarted at startFrame; same valid-prefix
 *   semantics as SQLite recovery apply
 * - Returns FOUND_EXACT_MATCH if the transaction at [startFrame, endFrame]
 *   exactly matches expectedDelta
 * - Returns CORRUPT only when the WAL header itself is invalid
 * - Returns NO_MATCH when the interval falls outside the valid prefix or does
 *   not describe exactly one committed transaction
 *
 * @param walBytes       Raw WAL file bytes
 * @param pageSize       Database page size
 * @param startFrame     First frame of the transaction (1-based)
 * @param endFrame       Last frame (commit frame) of the transaction
 * @param walSalt0       Expected WAL salt-0
 * @param walSalt1       Expected WAL salt-1
 * @param expectedDelta  The delta we expect to find
 * @param hashPage       Function to hash a page image → PageHash
 */
export function scanExactCommittedTransaction(
    walBytes: Uint8Array,
    pageSize: number,
    startFrame: number,
    endFrame: number,
    walSalt0: number,
    walSalt1: number,
    expectedDelta: Delta,
    hashPage: HashPageFn
): ExactScanResult {
    if (startFrame < 1 || endFrame < startFrame) {
        return { kind: 'NO_MATCH' }
    }

    const validHi = buildValidWalPrefix(walBytes, endFrame, pageSize)

    // If the valid prefix doesn't reach endFrame, the append did not complete
    // as a visible committed transaction in the durably valid WAL prefix.
    if (validHi < endFrame) {
        if (validHi < 0) return { kind: 'CORRUPT' }
        return { kind: 'NO_MATCH' }
    }

    // Verify salts match
    const header = parseWalHeader(walBytes.subarray(0, WAL_HDR_SIZE))
    if (header == null) return { kind: 'CORRUPT' }
    if (header.salt1 !== walSalt0 || header.salt2 !== walSalt1) {
        return { kind: 'NO_MATCH' }
    }

    const frameSize = WAL_FRAME_HDR_SIZE + pageSize

    // Exact interval means startFrame must begin at a transaction boundary.
    if (startFrame > 1) {
        const prevFrameOff = WAL_HDR_SIZE + (startFrame - 2) * frameSize
        const prevView = new DataView(
            walBytes.buffer, walBytes.byteOffset + prevFrameOff, WAL_FRAME_HDR_SIZE
        )
        if (prevView.getUint32(4, false) === 0) {
            return { kind: 'NO_MATCH' }
        }
    }

    const pageHashes = new Map<Pgno, PageHash>()
    let commitNPage: number | null = null

    for (let frameIdx = startFrame; frameIdx <= endFrame; frameIdx++) {
        const frameOff = WAL_HDR_SIZE + (frameIdx - 1) * frameSize
        if (frameOff + frameSize > walBytes.length) {
            return { kind: 'NO_MATCH' }
        }

        const fhView = new DataView(
            walBytes.buffer, walBytes.byteOffset + frameOff, WAL_FRAME_HDR_SIZE
        )
        const pgno = fhView.getUint32(0, false)
        const nPage = fhView.getUint32(4, false)

        if (pgno < 1) return { kind: 'CORRUPT' }

        const pageData = new Uint8Array(
            walBytes.buffer,
            walBytes.byteOffset + frameOff + WAL_FRAME_HDR_SIZE,
            pageSize
        ).slice()

        const hash = hashPage(pageData)
        // Last-write-wins for duplicate pgno within same transaction
        pageHashes.set(pgno as Pgno, hash)

        if (nPage > 0) {
            if (frameIdx !== endFrame) {
                return { kind: 'NO_MATCH' }
            }
            commitNPage = nPage
        } else if (frameIdx === endFrame) {
            return { kind: 'NO_MATCH' }
        }
    }

    // Compare deltas
    const foundDelta: Delta = {
        pageHashes,
        postPageCount: commitNPage,
    }

    if (equalDelta(foundDelta, expectedDelta)) {
        return {
            kind: 'FOUND_EXACT_MATCH',
            frameMax: endFrame as FrameMax,
        }
    }

    return { kind: 'NO_MATCH' }
}

// ── §7.7 txMatchesIgnoredIntent ─────────────────────────────────

/**
 * Check if a recovered WAL transaction matches an ignored reconcile intent.
 *
 * §7.7: Used during ghost recovery to skip transactions that belong to
 * an already-absorbed or deliberately-ignored reconcile intent.
 */
export function txMatchesIgnoredIntent(
    ignoredIntent: {
        phase: string
        importDelta: Delta
        commitFrameMax?: number | null
        commitPostWalSalt0?: number | null
        commitPostWalSalt1?: number | null
        attemptEndFrame?: number | null
        attemptWalSalt0?: number | null
        attemptWalSalt1?: number | null
    } | null,
    liveWalSalt0: number,
    liveWalSalt1: number,
    rec: { frameMax: number; delta: Delta }
): boolean {
    if (ignoredIntent == null) return false

    if (ignoredIntent.phase === 'COMMITTED') {
        return (
            ignoredIntent.commitPostWalSalt0 === liveWalSalt0
            && ignoredIntent.commitPostWalSalt1 === liveWalSalt1
            && ignoredIntent.commitFrameMax === rec.frameMax
            && equalDelta(ignoredIntent.importDelta, rec.delta)
        )
    }

    if (ignoredIntent.phase === 'APPENDING') {
        return (
            ignoredIntent.attemptWalSalt0 === liveWalSalt0
            && ignoredIntent.attemptWalSalt1 === liveWalSalt1
            && ignoredIntent.attemptEndFrame === rec.frameMax
            && equalDelta(ignoredIntent.importDelta, rec.delta)
        )
    }

    return false
}

// ── Delta equality ───────────────────────────────────────────────

/**
 * Structural delta equality — compares pageHashes maps and postPageCount.
 */
export function equalDelta(a: Delta, b: Delta): boolean {
    if (a.postPageCount !== b.postPageCount) return false
    if (a.pageHashes.size !== b.pageHashes.size) return false
    for (const [pgno, hash] of a.pageHashes) {
        if (b.pageHashes.get(pgno) !== hash) return false
    }
    return true
}

// ── WAL builder for testing ──────────────────────────────────────

/**
 * Build a valid WAL buffer with the given transactions.
 * Each transaction is an array of {pgno, pageData?, nPage?}.
 * The last entry in each transaction gets nPage = dbPageCount (commit marker).
 *
 * This uses the bit-exact checksum algorithm from walChecksum.ts.
 */
export function buildTestWal(
    pageSize: number,
    salt1: number,
    salt2: number,
    transactions: Array<Array<{
        pgno: number
        pageData?: Uint8Array
        nPage?: number
    }>>,
    dbPageCount: number = 0
): Uint8Array {
    const frameSize = WAL_FRAME_HDR_SIZE + pageSize
    let totalFrames = 0
    for (const tx of transactions) {
        totalFrames += tx.length
    }

    const totalSize = WAL_HDR_SIZE + totalFrames * frameSize
    const wal = new Uint8Array(totalSize)

    // Write WAL header
    const hdrView = new DataView(wal.buffer)
    hdrView.setUint32(0, WAL_MAGIC_LE, false)
    hdrView.setUint32(4, WAL_FORMAT_VERSION, false)
    hdrView.setUint32(8, pageSize, false)
    hdrView.setUint32(12, 0, false) // nCkpt
    hdrView.setUint32(16, salt1, false)
    hdrView.setUint32(20, salt2, false)

    // Compute header checksum
    const [hc1, hc2] = walChecksumBytes(true, wal.subarray(0, 24), [0, 0])
    hdrView.setUint32(24, hc1, false)
    hdrView.setUint32(28, hc2, false)

    let runningCksum: [number, number] = [hc1, hc2]
    let frameIdx = 0

    for (const tx of transactions) {
        for (let i = 0; i < tx.length; i++) {
            const entry = tx[i]
            const isCommit = i === tx.length - 1
            const nPage = entry.nPage ?? (isCommit ? dbPageCount : 0)
            const pageData = entry.pageData ?? new Uint8Array(pageSize)

            const frameOff = WAL_HDR_SIZE + frameIdx * frameSize

            // Build frame header bytes for checksum: pgno + nPage
            const hdr8 = new Uint8Array(8)
            const hdr8View = new DataView(hdr8.buffer)
            hdr8View.setUint32(0, entry.pgno, false)
            hdr8View.setUint32(4, nPage, false)

            const afterHdr8 = walChecksumBytes(true, hdr8, runningCksum)
            const computed = walChecksumBytes(true, pageData, afterHdr8)
            runningCksum = computed

            // Write frame header
            const fhView = new DataView(wal.buffer, frameOff, WAL_FRAME_HDR_SIZE)
            fhView.setUint32(0, entry.pgno, false)
            fhView.setUint32(4, nPage, false)
            fhView.setUint32(8, salt1, false)
            fhView.setUint32(12, salt2, false)
            fhView.setUint32(16, computed[0], false)
            fhView.setUint32(20, computed[1], false)

            // Write page data
            wal.set(pageData, frameOff + WAL_FRAME_HDR_SIZE)

            frameIdx++
        }
    }

    return wal
}
